import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, CalendarBlank, CheckCircle, Copy, CornersIn, CornersOut, Desktop, Eye, MapPin, Play, ShieldWarning, Spinner, Stop, Timer, Users,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fmtError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import CorrectAnswerDirection from "../components/CorrectAnswerDirection";
import ExamEntryControls from "../components/ExamEntryControls";
import OnlineExamDashboard from "../components/OnlineExamDashboard";

const AUDIENCES = [
  {
    value: "registered_only",
    title: "Somente alunos cadastrados",
    description: "Exige login e valida se o aluno pertence a uma turma desta prova.",
  },
  {
    value: "allow_guests",
    title: "Cadastrados ou não cadastrados",
    description: "Alunos existentes entram pela conta; os demais registram nome, CPF e e-mail para esta prova.",
  },
];

const SESSION_MODES = [
  {
    value: "classroom_live",
    title: "Correção ao vivo em sala",
    description: "O professor projeta uma questão por vez, revela a resposta e controla o avanço.",
  },
  {
    value: "online_exam",
    title: "Prova online individual",
    description: "Cada aluno tem seu relógio, avança sem retorno e pode usar fiscalização assistida pela câmera.",
  },
];

const RESULT_MODES = [
  { value: "hidden", title: "Não mostrar resultados", description: "A projeção nunca mostra a distribuição das respostas." },
  { value: "aggregate_live", title: "Mostrar em tempo real", description: "Exibe os totais agregados enquanto as respostas chegam." },
  { value: "after_reveal", title: "Mostrar após revelar", description: "Oculta a distribuição até o professor revelar a resposta." },
];

function localDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

function futureDateTime(minutes) {
  return localDateTime(new Date(Date.now() + minutes * 60000));
}

function OnlineSettingsEditor({ session, onChange }) {
  const [mode, setMode] = useState(session.availability_mode || "manual_deadline");
  const [opensAt, setOpensAt] = useState(localDateTime(session.entry_opens_at));
  const [closesAt, setClosesAt] = useState(localDateTime(session.entry_deadline_at));
  const [totalMinutes, setTotalMinutes] = useState(String(session.total_time_minutes || 120));
  const [questionSeconds, setQuestionSeconds] = useState(session.per_question_time_seconds ? String(session.per_question_time_seconds) : "");
  const [singleScreenRequired, setSingleScreenRequired] = useState(Boolean(session.single_screen_required));
  const [proctoringEnabled, setProctoringEnabled] = useState(Boolean(session.proctoring_enabled));
  const [locationRequired, setLocationRequired] = useState(Boolean(session.location_required));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setMode(session.availability_mode || "manual_deadline");
    setOpensAt(localDateTime(session.entry_opens_at));
    setClosesAt(localDateTime(session.entry_deadline_at));
    setTotalMinutes(String(session.total_time_minutes || 120));
    setQuestionSeconds(session.per_question_time_seconds ? String(session.per_question_time_seconds) : "");
    setSingleScreenRequired(Boolean(session.single_screen_required));
    setProctoringEnabled(Boolean(session.proctoring_enabled));
    setLocationRequired(Boolean(session.location_required));
  }, [session.id, session.availability_mode, session.entry_opens_at, session.entry_deadline_at, session.total_time_minutes, session.per_question_time_seconds, session.single_screen_required, session.proctoring_enabled, session.location_required]);

  const save = async () => {
    setSaving(true);
    try {
      const response = await api.put(`/live-sessions/${session.id}/online-settings`, {
        availability_mode: mode,
        entry_opens_at: mode === "scheduled_window" && opensAt ? new Date(opensAt).toISOString() : null,
        entry_deadline_at: closesAt ? new Date(closesAt).toISOString() : null,
        total_time_minutes: Number(totalMinutes),
        per_question_time_seconds: questionSeconds ? Number(questionSeconds) : null,
        single_screen_required: singleScreenRequired,
        proctoring_enabled: proctoringEnabled,
        location_required: locationRequired,
      });
      onChange(response.data);
      toast.success("Configurações da prova atualizadas.");
    } catch (error) {
      toast.error(fmtError(error));
    } finally {
      setSaving(false);
    }
  };

  return <section className="border border-primary bg-primary/5 p-5" data-testid="online-settings-editor">
    <div className="flex flex-wrap justify-between gap-3">
      <div><div className="label-eyebrow text-primary">configuração ainda editável</div><h2 className="font-bold mt-1">Liberação e relógios da prova</h2><p className="text-sm text-muted-foreground mt-1">Você pode alterar estes dados até a primeira tentativa começar.</p></div>
      <Button onClick={save} disabled={saving || Number(totalMinutes) < 1 || (mode === "scheduled_window" && (!opensAt || !closesAt))} className="rounded-none">{saving && <Spinner className="animate-spin mr-2" />}Salvar alterações</Button>
    </div>
    <div className="grid md:grid-cols-2 gap-3 mt-5">
      <button type="button" onClick={() => setMode("scheduled_window")} className={`border p-4 text-left ${mode === "scheduled_window" ? "border-primary bg-card" : "border-border"}`}><b>Janela programada</b><span className="block text-xs text-muted-foreground mt-1">O aluno pode iniciar automaticamente entre dois horários.</span></button>
      <button type="button" onClick={() => setMode("manual_deadline")} className={`border p-4 text-left ${mode === "manual_deadline" ? "border-primary bg-card" : "border-border"}`}><b>Abertura manual + limite</b><span className="block text-xs text-muted-foreground mt-1">Você abre a prova e pode definir até quando aceitar novas entradas.</span></button>
    </div>
    <div className={`grid gap-4 mt-4 ${mode === "scheduled_window" ? "md:grid-cols-2" : "md:grid-cols-1"}`}>
      {mode === "scheduled_window" && <label><span className="label-eyebrow">disponível para iniciar a partir de</span><Input type="datetime-local" value={opensAt} onChange={(event) => setOpensAt(event.target.value)} className="rounded-none mt-2 bg-card" /></label>}
      <label><span className="label-eyebrow">{mode === "scheduled_window" ? "início permitido até" : "limite para novas entradas (opcional)"}</span><Input type="datetime-local" value={closesAt} onChange={(event) => setClosesAt(event.target.value)} className="rounded-none mt-2 bg-card" /></label>
    </div>
    <div className="grid md:grid-cols-2 gap-4 mt-4">
      <label><span className="label-eyebrow">tempo máximo da prova (minutos)</span><Input type="number" min="1" max="720" value={totalMinutes} onChange={(event) => setTotalMinutes(event.target.value)} className="rounded-none mt-2 bg-card" /></label>
      <label><span className="label-eyebrow">limite por questão (segundos, opcional)</span><Input type="number" min="15" max="7200" value={questionSeconds} onChange={(event) => setQuestionSeconds(event.target.value)} className="rounded-none mt-2 bg-card" placeholder="Sem limite por questão" /></label>
    </div>
    <label className={`flex gap-3 mt-4 cursor-pointer border p-4 ${singleScreenRequired ? "border-primary bg-card" : "border-border"}`}>
      <input type="checkbox" checked={singleScreenRequired} onChange={(event) => setSingleScreenRequired(event.target.checked)} className="mt-1" />
      <Desktop size={25} className="text-primary shrink-0" />
      <span><b>Exigir somente um monitor ativo</b><span className="block text-sm text-muted-foreground mt-1">O aluno autoriza a tela inteira escolhida. Se sair dela ou ligar outro monitor, a questão avança sem retorno, a captura da tela é registrada e a prova fica bloqueada.</span></span>
    </label>
    <div className="grid md:grid-cols-2 gap-3 mt-4">
      <label className={`flex gap-3 cursor-pointer border p-4 ${proctoringEnabled ? "border-primary bg-card" : "border-border"}`}>
        <input type="checkbox" checked={proctoringEnabled} onChange={(event) => setProctoringEnabled(event.target.checked)} className="mt-1" />
        <ShieldWarning size={25} className="text-amber-600 shrink-0" />
        <span><b>Exigir câmera</b><span className="block text-sm text-muted-foreground mt-1">Sem câmera ativa, a prova não inicia e fica bloqueada se ela for desligada. Uma foto é enviada no início, em cada questão e em cada ocorrência.</span></span>
      </label>
      <label className={`flex gap-3 border p-4 ${session.geofence_enabled ? "cursor-not-allowed opacity-80" : "cursor-pointer"} ${locationRequired ? "border-primary bg-card" : "border-border"}`}>
        <input type="checkbox" checked={locationRequired} disabled={Boolean(session.geofence_enabled)} onChange={(event) => setLocationRequired(event.target.checked)} className="mt-1" />
        <MapPin size={25} className="text-primary shrink-0" />
        <span><b>Exigir localização</b><span className="block text-sm text-muted-foreground mt-1">Sem localização ativa, a prova não inicia e fica bloqueada se ela for desativada.{session.geofence_enabled ? " Obrigatória porque há limite por distância." : ""}</span></span>
      </label>
    </div>
  </section>;
}

function Distribution({ items = [], alternatives = [], revealed = false }) {
  const textByLetter = Object.fromEntries((alternatives || []).map((item) => [item.letter, item.text]));
  return (
    <div className="space-y-3">
      {items.map((item) => (
        <div key={item.letter}>
          <div className="flex items-center justify-between gap-3 text-sm mb-1">
            <span className="truncate"><b>{item.letter}</b> · {textByLetter[item.letter]}</span>
            <span className="font-mono-data shrink-0">{item.count} · {item.percentage}%</span>
          </div>
          <div className="h-7 border border-border bg-muted/20 overflow-hidden">
            <div
              className={`h-full transition-all duration-500 ${
                revealed && alternatives.find((option) => option.letter === item.letter)?.is_correct
                  ? "bg-emerald-500" : "bg-primary"
              }`}
              style={{ width: `${Math.max(0, Math.min(100, item.percentage || 0))}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function StaffQuestionMedia({ id, compact = false }) {
  if (!id) return null;
  return <img src={`/api/question-media/${id}`} alt="Ilustração da questão" className={`${compact ? "max-h-36" : "max-h-80"} max-w-full object-contain mx-auto my-4`} />;
}

export default function LivePresenterPage() {
  const { examId } = useParams();
  const [exam, setExam] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [acting, setActing] = useState(false);
  const [audienceMode, setAudienceMode] = useState("registered_only");
  const [sessionMode, setSessionMode] = useState("classroom_live");
  const [resultDisplayMode, setResultDisplayMode] = useState("aggregate_live");
  const [totalTimeMinutes, setTotalTimeMinutes] = useState("120");
  const [perQuestionTimeSeconds, setPerQuestionTimeSeconds] = useState("");
  const [availabilityMode, setAvailabilityMode] = useState("scheduled_window");
  const [entryOpensAt, setEntryOpensAt] = useState(() => futureDateTime(15));
  const [entryDeadlineAt, setEntryDeadlineAt] = useState(() => futureDateTime(195));
  const [proctoringEnabled, setProctoringEnabled] = useState(false);
  const [singleScreenRequired, setSingleScreenRequired] = useState(true);
  const [locationRequired, setLocationRequired] = useState(true);
  const [geofenceEnabled, setGeofenceEnabled] = useState(false);
  const [geofenceLatitude, setGeofenceLatitude] = useState(null);
  const [geofenceLongitude, setGeofenceLongitude] = useState(null);
  const [geofenceRadius, setGeofenceRadius] = useState("1");
  const [capturingLocation, setCapturingLocation] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const polling = useRef(false);
  const stageRef = useRef(null);

  const poll = useCallback(async (sessionId, quiet = true) => {
    if (!sessionId || polling.current) return;
    polling.current = true;
    try {
      const response = await api.get(`/live-sessions/${sessionId}/presenter`);
      setSession(response.data);
    } catch (error) {
      if (!quiet) toast.error(fmtError(error));
    } finally {
      polling.current = false;
    }
  }, []);

  useEffect(() => {
    let active = true;
    Promise.all([api.get(`/exams/${examId}`), api.get(`/exams/${examId}/live-sessions`)])
      .then(([examResponse, sessionsResponse]) => {
        if (!active) return;
        setExam(examResponse.data);
        const latest = (sessionsResponse.data.items || [])[0];
        if (latest) poll(latest.id, false);
      })
      .catch((error) => toast.error(fmtError(error)))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [examId, poll]);

  useEffect(() => {
    if (!session?.id || session.phase === "finished") return undefined;
    const timer = window.setInterval(() => poll(session.id), session.session_mode === "online_exam" ? 2000 : 800);
    return () => window.clearInterval(timer);
  }, [session?.id, session?.phase, session?.session_mode, poll]);

  useEffect(() => {
    const update = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", update);
    return () => document.removeEventListener("fullscreenchange", update);
  }, []);

  const captureExamLocation = async () => {
    if (!navigator.geolocation) {
      toast.error("Este navegador não oferece geolocalização.");
      return;
    }
    setCapturingLocation(true);
    navigator.geolocation.getCurrentPosition((position) => {
      setGeofenceLatitude(position.coords.latitude);
      setGeofenceLongitude(position.coords.longitude);
      setCapturingLocation(false);
      toast.success("Local da prova registrado");
    }, () => {
      setCapturingLocation(false);
      toast.error("Não foi possível obter o local. Autorize a localização no navegador.");
    }, { enableHighAccuracy: true, timeout: 20000, maximumAge: 30000 });
  };

  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else if (stageRef.current?.requestFullscreen) await stageRef.current.requestFullscreen();
      else toast.error("O navegador não oferece apresentação em tela inteira.");
    } catch (error) {
      toast.error(error?.message || "Não foi possível abrir a tela inteira");
    }
  };

  const createSession = async () => {
    setCreating(true);
    try {
      const response = await api.post(`/exams/${examId}/live-sessions`, {
        audience_mode: sessionMode === "online_exam" ? "registered_only" : audienceMode,
        session_mode: sessionMode,
        result_display_mode: resultDisplayMode,
        total_time_minutes: sessionMode === "online_exam" ? Number(totalTimeMinutes || 0) : null,
        per_question_time_seconds: sessionMode === "online_exam" && perQuestionTimeSeconds ? Number(perQuestionTimeSeconds) : null,
        availability_mode: sessionMode === "online_exam" ? availabilityMode : "manual_deadline",
        entry_opens_at: sessionMode === "online_exam" && availabilityMode === "scheduled_window" && entryOpensAt ? new Date(entryOpensAt).toISOString() : null,
        entry_deadline_at: sessionMode === "online_exam" && entryDeadlineAt ? new Date(entryDeadlineAt).toISOString() : null,
        proctoring_enabled: sessionMode === "online_exam" && proctoringEnabled,
        single_screen_required: sessionMode === "online_exam" && singleScreenRequired,
        location_required: sessionMode === "online_exam" && (locationRequired || geofenceEnabled),
        geofence_enabled: geofenceEnabled,
        geofence_latitude: geofenceEnabled ? geofenceLatitude : null,
        geofence_longitude: geofenceEnabled ? geofenceLongitude : null,
        geofence_radius_km: Number(geofenceRadius || 1),
      });
      setSession(response.data);
      toast.success(sessionMode === "online_exam" ? "Prova online criada. O acesso já pode ser enviado aos alunos." : "Sala criada. O QR já pode ser projetado.");
    } catch (error) {
      toast.error(fmtError(error));
    } finally {
      setCreating(false);
    }
  };

  const control = async (action) => {
    if (!session?.id) return;
    setActing(true);
    try {
      const response = await api.post(`/live-sessions/${session.id}/control`, { action });
      setSession(response.data);
      if (action === "reveal") toast.success("Respostas bloqueadas. Gabarito revelado.");
      if (action === "finish") toast.success(session?.session_mode === "online_exam" ? "Prova encerrada e resultados enviados para as análises." : "Aula encerrada e resultados enviados para as análises.");
    } catch (error) {
      toast.error(fmtError(error));
      await poll(session.id);
    } finally {
      setActing(false);
    }
  };

  const copyJoin = async () => {
    await navigator.clipboard.writeText(session.join_url);
    toast.success("Link copiado");
  };

  const copyDisplay = async () => {
    await navigator.clipboard.writeText(session.display_url);
    toast.success("Link da projeção copiado");
  };

  if (loading) return <div className="p-16 label-eyebrow">preparando sala ao vivo…</div>;

  if (!session) {
    return (
      <main className="max-w-5xl mx-auto px-6 py-10">
        <Link to="/exams" className="inline-flex items-center gap-2 text-xs font-mono-data uppercase tracking-widest text-primary">
          <ArrowLeft /> Voltar às provas
        </Link>
        <div className="mt-8 border border-border bg-card p-8">
          <div className="label-eyebrow">nova aplicação</div>
          <h1 className="font-display font-black text-4xl tracking-tight mt-2">{exam?.name}</h1>
          <p className="text-muted-foreground mt-3 max-w-2xl">
            Escolha entre correção projetada em sala ou prova online individual com relógio, avanço sem retorno e fiscalização assistida opcional.
          </p>
          <div className="grid md:grid-cols-2 gap-4 mt-8" data-testid="live-session-mode-picker">
            {SESSION_MODES.map((item) => <button type="button" key={item.value} onClick={() => { setSessionMode(item.value); if (item.value === "online_exam") setAudienceMode("registered_only"); }} className={`border p-5 text-left ${sessionMode === item.value ? "border-primary bg-primary/5" : "border-border hover:border-foreground"}`}><div className="font-bold">{item.title}</div><div className="text-sm text-muted-foreground mt-2">{item.description}</div></button>)}
          </div>

          {sessionMode === "classroom_live" && <div className="mt-7">
            <div className="label-eyebrow">resultados na tela projetada</div>
            <div className="grid md:grid-cols-3 gap-3 mt-3" data-testid="live-result-display-picker">
              {RESULT_MODES.map((item) => <button type="button" key={item.value} onClick={() => setResultDisplayMode(item.value)} className={`border p-4 text-left ${resultDisplayMode === item.value ? "border-primary bg-primary/5" : "border-border"}`}><div className="font-bold text-sm">{item.title}</div><div className="text-xs text-muted-foreground mt-2">{item.description}</div></button>)}
            </div>
          </div>}

          {sessionMode === "online_exam" && <div className="border border-primary bg-primary/5 p-5 mt-6" data-testid="online-exam-settings">
            <div className="flex gap-3"><CalendarBlank size={28} className="text-primary shrink-0" /><div><b>Como esta prova será liberada?</b><p className="text-sm text-muted-foreground mt-1">Escolha uma janela automática ou abra manualmente com um horário-limite para novas entradas.</p></div></div>
            <div className="grid md:grid-cols-2 gap-3 mt-5">
              <button type="button" onClick={() => setAvailabilityMode("scheduled_window")} className={`border p-4 text-left ${availabilityMode === "scheduled_window" ? "border-primary bg-card" : "border-border"}`}><b>Janela programada</b><span className="block text-xs text-muted-foreground mt-1">O aluno inicia somente entre o horário inicial e o horário final.</span></button>
              <button type="button" onClick={() => setAvailabilityMode("manual_deadline")} className={`border p-4 text-left ${availabilityMode === "manual_deadline" ? "border-primary bg-card" : "border-border"}`}><b>Abertura manual + limite</b><span className="block text-xs text-muted-foreground mt-1">Você clica em abrir e define até quando novas pessoas podem entrar.</span></button>
            </div>
            <div className={`grid gap-4 mt-5 ${availabilityMode === "scheduled_window" ? "md:grid-cols-2" : "md:grid-cols-1"}`}>
              {availabilityMode === "scheduled_window" && <label><span className="label-eyebrow">disponível para iniciar a partir de</span><Input type="datetime-local" value={entryOpensAt} onChange={(event) => setEntryOpensAt(event.target.value)} className="rounded-none mt-2 bg-card" /></label>}
              <label><span className="label-eyebrow">{availabilityMode === "scheduled_window" ? "início permitido até" : "limite para novas entradas (opcional)"}</span><Input type="datetime-local" value={entryDeadlineAt} onChange={(event) => setEntryDeadlineAt(event.target.value)} className="rounded-none mt-2 bg-card" /></label>
            </div>
            <div className="flex gap-3 mt-6"><Timer size={28} className="text-primary shrink-0" /><div><b>Relógios e avanço individual</b><p className="text-sm text-muted-foreground mt-1">O tempo é validado também no servidor. Ao responder, pular ou esgotar o relógio, a questão é encerrada e não volta.</p></div></div>
            <div className="grid md:grid-cols-2 gap-4 mt-5">
              <label><span className="label-eyebrow">tempo total (minutos)</span><Input type="number" min="1" max="720" value={totalTimeMinutes} onChange={(event) => setTotalTimeMinutes(event.target.value)} className="rounded-none mt-2" /></label>
              <label><span className="label-eyebrow">tempo por questão (segundos, opcional)</span><Input type="number" min="15" max="7200" value={perQuestionTimeSeconds} onChange={(event) => setPerQuestionTimeSeconds(event.target.value)} className="rounded-none mt-2" placeholder="Sem limite individual" /></label>
            </div>
            <label className="flex gap-3 mt-5 cursor-pointer border border-border bg-card p-4"><input type="checkbox" checked={proctoringEnabled} onChange={(event) => setProctoringEnabled(event.target.checked)} className="mt-1" /><ShieldWarning size={25} className="text-amber-600 shrink-0" /><span><b>Ativar fiscalização assistida pela câmera</b><span className="block text-sm text-muted-foreground mt-1">O reconhecimento facial ocorre no aparelho. O servidor recebe uma foto no início, em cada questão e em cada ocorrência. Não há punição automática.</span></span></label>
            <label className={`flex gap-3 mt-4 cursor-pointer border p-4 ${singleScreenRequired ? "border-primary bg-card" : "border-border"}`}><input type="checkbox" checked={singleScreenRequired} onChange={(event) => setSingleScreenRequired(event.target.checked)} className="mt-1" /><Desktop size={25} className="text-primary shrink-0" /><span><b>Exigir somente um monitor ativo</b><span className="block text-sm text-muted-foreground mt-1">O aluno autoriza a captura da tela inteira escolhida. Sair da tela ou ligar outro monitor encerra a questão sem retorno, registra a captura e bloqueia a prova até a regularização.</span></span></label>
            <label className={`flex gap-3 mt-4 cursor-pointer border p-4 ${locationRequired || geofenceEnabled ? "border-primary bg-card" : "border-border"}`}><input type="checkbox" checked={locationRequired || geofenceEnabled} disabled={geofenceEnabled} onChange={(event) => setLocationRequired(event.target.checked)} className="mt-1" /><MapPin size={25} className="text-primary shrink-0" /><span><b>Exigir localização durante a prova</b><span className="block text-sm text-muted-foreground mt-1">Se marcada, a prova não inicia sem localização e fica bloqueada caso ela seja desativada.{geofenceEnabled ? " Esta exigência está ligada porque há limite por distância." : ""}</span></span></label>
          </div>}

          {sessionMode === "classroom_live" && <>
          <div className="label-eyebrow mt-7">quem pode participar</div>
          <div className="grid md:grid-cols-2 gap-4 mt-8">
            {AUDIENCES.map((item) => (
              <button
                key={item.value}
                onClick={() => setAudienceMode(item.value)}
                className={`border p-5 text-left transition-colors ${audienceMode === item.value ? "border-primary bg-primary/5" : "border-border hover:border-foreground"}`}
              >
                <div className="font-bold">{item.title}</div>
                <div className="text-sm text-muted-foreground mt-2">{item.description}</div>
              </button>
            ))}
          </div>
          </>}
          <div className={`border p-5 mt-5 ${geofenceEnabled ? "border-primary bg-primary/5" : "border-border"}`}>
            <label className="flex gap-3 items-start cursor-pointer">
              <input type="checkbox" checked={geofenceEnabled} onChange={(event) => { setGeofenceEnabled(event.target.checked); if (event.target.checked) setLocationRequired(true); }} className="mt-1" />
              <span><b>{sessionMode === "online_exam" ? "Também limitar por distância do local da prova" : "Exigir presença próxima ao local da prova"}</b><span className="block text-sm text-muted-foreground mt-1">{sessionMode === "online_exam" ? "Ative esta regra para impedir o início fora do raio definido; a localização passa a ser obrigatória." : "O aluno precisa autorizar a localização e estar dentro da distância máxima definida."}</span></span>
            </label>
            {geofenceEnabled && (
              <div className="grid md:grid-cols-[1fr_180px] gap-3 mt-5">
                <div>
                  <Button type="button" variant="outline" onClick={captureExamLocation} disabled={capturingLocation} className="rounded-none w-full">
                    {capturingLocation ? <Spinner className="animate-spin mr-2" /> : <MapPin className="mr-2" weight="fill" />} Registrar este local
                  </Button>
                  {geofenceLatitude != null && <div className="text-xs font-mono-data text-emerald-700 mt-2">Local salvo · {geofenceLatitude.toFixed(5)}, {geofenceLongitude.toFixed(5)}</div>}
                </div>
                <label>
                  <span className="label-eyebrow">distância máxima (km)</span>
                  <Input type="number" min="0.02" max="100" step="0.05" value={geofenceRadius} onChange={(event) => setGeofenceRadius(event.target.value)} className="rounded-none mt-2" />
                </label>
              </div>
            )}
          </div>
          <Button onClick={createSession} disabled={creating || (sessionMode === "online_exam" && (Number(totalTimeMinutes) < 1 || (availabilityMode === "scheduled_window" && (!entryOpensAt || !entryDeadlineAt)))) || (geofenceEnabled && (geofenceLatitude == null || Number(geofenceRadius) < 0.02))} className="rounded-none mt-8 font-mono-data uppercase tracking-widest">
            {creating ? <Spinner className="mr-2 animate-spin" /> : <Play className="mr-2" />} {sessionMode === "online_exam" ? "Criar prova online" : "Criar sala e gerar QR"}
          </Button>
        </div>
      </main>
    );
  }

  const question = session.question;
  const alternatives = question?.alternatives || [];
  const atLast = session.current_position >= session.total_questions;
  const onlineSession = session.session_mode === "online_exam";

  return (
    <main ref={stageRef} className={`${isFullscreen ? "w-screen min-h-screen overflow-y-auto bg-background px-8 py-6" : "max-w-[1500px] mx-auto px-5 py-6"}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        {isFullscreen ? <div className="font-display text-xl font-black">SAKI<span className="text-primary">/</span>Xm · apresentação</div> : (
          <Link to="/exams" className="inline-flex items-center gap-2 text-xs font-mono-data uppercase tracking-widest text-primary">
            <ArrowLeft /> Voltar às provas
          </Link>
        )}
        <div className="flex gap-2">
          {session.phase === "finished" && (
            <Button variant="outline" className="rounded-none" onClick={() => setSession(null)}>Nova sessão</Button>
          )}
          {!onlineSession && <Button variant={isFullscreen ? "default" : "outline"} className="rounded-none" onClick={toggleFullscreen}>
            {isFullscreen ? <CornersIn className="mr-2" /> : <CornersOut className="mr-2" />} {isFullscreen ? "Sair da tela inteira" : "Apresentar em tela inteira"}
          </Button>}
          <Button variant="outline" className="rounded-none" onClick={copyJoin}><Copy className="mr-2" />Copiar link</Button>
        </div>
      </div>

      {!onlineSession && <div className="mb-5 border border-primary bg-primary/5 p-4 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4" data-testid="remote-display-access">
        <div>
          <div className="label-eyebrow text-primary">projeção sem login · controle remoto ativo</div>
          <div className="mt-2 text-sm text-muted-foreground">
            No computador da sala, abra <b>saki-x.net/apresentar</b> e digite
            <span className="ml-2 font-mono-data text-xl font-black tracking-[0.14em] text-foreground">{session.display_code}</span>.
            Continue controlando as questões por esta tela no seu celular.
          </div>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          <Button variant="outline" className="rounded-none" onClick={copyDisplay}><Copy className="mr-2" />Copiar projeção</Button>
          <a href={session.display_url} target="_blank" rel="noreferrer noopener" className="inline-flex items-center border border-primary bg-primary px-4 py-2 text-primary-foreground text-sm font-medium">Abrir tela pública</a>
        </div>
      </div>}

      {onlineSession && <div className="mb-5 border border-primary bg-primary/5 p-4 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4"><div><div className="label-eyebrow text-primary">prova online individual</div><div className="text-sm text-muted-foreground mt-2">Envie o link ou código aos alunos. Cada tentativa terá {session.total_time_minutes} minutos{session.per_question_time_seconds ? ` e ${session.per_question_time_seconds} segundos por questão` : ""}, sem retorno.</div></div><div className="font-mono-data text-3xl font-black tracking-widest">{session.join_code}</div></div>}

      {!isFullscreen && onlineSession && !session.has_attempt_started && <div className="mb-5"><OnlineSettingsEditor session={session} onChange={setSession} /></div>}
      {!isFullscreen && <div className="mb-5"><ExamEntryControls session={session} onChange={setSession} /></div>}

      {session.phase === "lobby" ? (
        <section className="grid lg:grid-cols-[1fr_420px] gap-6">
          <div className={`border border-border bg-card p-10 flex flex-col justify-center ${isFullscreen ? "min-h-[calc(100vh-105px)]" : "min-h-[620px]"}`}>
            <div className="label-eyebrow">{onlineSession ? "prova online · aguardando abertura" : "sala de aula · aguardando"}</div>
            <h1 className="font-display font-black text-5xl mt-3">{session.exam_name}</h1>
            <div className="font-mono-data text-7xl font-black tracking-[0.14em] text-primary mt-10">{session.join_code}</div>
            <div className="mt-8 flex items-center gap-3 text-2xl"><Users weight="duotone" /> {session.participant_count} participante(s)</div>
            <p className="mt-3 text-muted-foreground">
              {onlineSession ? `Somente alunos cadastrados. Cada aluno prepara as exigências escolhidas${session.proctoring_enabled ? " (câmera" : ""}${session.proctoring_enabled && session.location_required ? " e localização)" : session.proctoring_enabled ? ")" : session.location_required ? " (localização)" : ""} e inicia sua tentativa após a abertura.` : session.audience_mode === "registered_only" ? "Somente alunos cadastrados e vinculados às turmas da prova." : "Alunos existentes entram pelo login; não cadastrados registram nome, CPF e e-mail."}
            </p>
            {session.location_required && <div className="mt-4 border border-amber-500 bg-amber-50 p-4 flex gap-3 items-center"><MapPin size={24} weight="fill" className="text-amber-700" /><div><b>Localização obrigatória</b><div className="text-sm text-muted-foreground">{session.geofence_enabled ? `Ingresso permitido em um raio máximo de ${session.geofence_radius_km} km; a localização deverá permanecer ativa.` : "A localização deverá permanecer ativa durante toda a tentativa, sem bloqueio por distância."}</div></div></div>}
            {onlineSession && session.availability_mode === "scheduled_window" ? <div className="mt-8 border border-primary bg-primary/5 p-5 w-fit"><div className="label-eyebrow text-primary">liberação automática programada</div><div className="font-display text-2xl font-black mt-2">{session.entry_opens_at ? new Date(session.entry_opens_at).toLocaleString("pt-BR") : "—"}</div><div className="text-sm text-muted-foreground mt-2">Início permitido até {session.entry_deadline_at ? new Date(session.entry_deadline_at).toLocaleString("pt-BR") : "—"}. Esta tela abrirá o painel automaticamente.</div></div> : <Button onClick={() => control("start")} disabled={acting || (!onlineSession && session.participant_count < 1)} className="rounded-none mt-10 h-14 text-lg font-mono-data uppercase tracking-widest w-fit">
              <Play className="mr-2" weight="fill" /> {onlineSession ? "Abrir prova online" : "Iniciar primeira questão"}
            </Button>}
          </div>
          <div className={`border border-border bg-white p-8 flex flex-col items-center justify-center ${isFullscreen ? "min-h-[calc(100vh-105px)]" : ""}`}>
            <img src={session.qr_code_data_url} alt="QR para entrar na prova ao vivo" className="w-full max-w-[340px] aspect-square" />
            <div className="font-mono-data text-sm break-all text-center mt-5">{session.join_url}</div>
          </div>
        </section>
      ) : onlineSession ? (
        <OnlineExamDashboard session={session} acting={acting} onSessionChange={setSession} onFinish={() => control("finish")} />
      ) : (
        <section className="grid xl:grid-cols-[1fr_390px] gap-5">
          <div className={`border border-border bg-card flex flex-col ${isFullscreen ? "min-h-[calc(100vh-105px)]" : "min-h-[680px]"}`}>
            <div className="border-b border-border px-8 py-5 flex flex-wrap justify-between gap-4">
              <div>
                <div className="label-eyebrow">{session.exam_name}</div>
                <div className="font-mono-data text-lg mt-1">Questão {session.current_position} de {session.total_questions}</div>
              </div>
              <div className={`px-4 py-2 border font-mono-data text-xs uppercase tracking-widest ${session.answer_revealed ? "border-emerald-600 text-emerald-700 bg-emerald-50" : "border-primary text-primary"}`}>
                {session.answer_revealed ? "envio bloqueado · resposta revelada" : "recebendo respostas"}
              </div>
            </div>
            {question && (
              <div className="p-8 md:p-12 flex-1">
                <div className="text-sm text-primary font-mono-data mb-5">{question.knowledge_area} · {question.system_context}</div>
                <h2 className={`${isFullscreen ? "text-4xl xl:text-5xl" : "text-2xl md:text-3xl"} font-semibold leading-relaxed`}>{question.stem}</h2>
                {(question.stem_media_ids || []).map((id) => <StaffQuestionMedia key={id} id={id} />)}
                <div className="grid md:grid-cols-2 gap-4 mt-10">
                  {alternatives.map((option) => {
                    const correct = session.answer_revealed && option.is_correct;
                    return (
                      <div key={option.letter} className={`border p-5 ${isFullscreen ? "text-2xl" : "text-lg"} ${correct ? "border-emerald-600 bg-emerald-50" : "border-border"}`}>
                        <b className={correct ? "text-emerald-700" : "text-primary"}>{option.letter}</b> · {option.text}
                        {option.media_id && <StaffQuestionMedia id={option.media_id} compact />}
                        {correct && <CheckCircle className="inline ml-3 text-emerald-700" weight="fill" />}
                      </div>
                    );
                  })}
                </div>
                {session.answer_revealed && <CorrectAnswerDirection alternatives={question.alternatives} className="mt-8 text-lg" />}
                {session.answer_revealed && question.explanation && (
                  <div className="mt-4 border-l-4 border-emerald-600 bg-emerald-50 p-5">
                    <b>Comentário:</b> {question.explanation}
                  </div>
                )}
              </div>
            )}
            <div className="border-t border-border p-5 flex flex-wrap justify-between gap-3">
              <div className="font-mono-data flex items-center gap-2"><Users /> {session.response_count}/{session.participant_count} responderam</div>
              {session.phase === "answering" && (
                <Button onClick={() => control("reveal")} disabled={acting} className="rounded-none bg-amber-500 hover:bg-amber-600 text-black font-mono-data uppercase tracking-widest">
                  <Eye className="mr-2" /> Revelar e bloquear
                </Button>
              )}
              {session.phase === "revealed" && !atLast && (
                <Button onClick={() => control("next")} disabled={acting} className="rounded-none font-mono-data uppercase tracking-widest">
                  Próxima questão <ArrowRight className="ml-2" />
                </Button>
              )}
              {session.phase === "revealed" && atLast && (
                <Button onClick={() => control("finish")} disabled={acting} className="rounded-none bg-destructive font-mono-data uppercase tracking-widest">
                  <Stop className="mr-2" weight="fill" /> Encerrar e registrar
                </Button>
              )}
            </div>
          </div>

          <aside className="space-y-5">
            <div className="border border-border bg-card p-5">
              <div className="label-eyebrow">respostas em tempo real</div>
              <div className="font-display text-4xl font-black mt-2">{session.response_count}<span className="text-muted-foreground text-xl">/{session.participant_count}</span></div>
              <div className="text-xs text-muted-foreground mt-2">{session.registered_participant_count} cadastrados · {session.guest_participant_count} convidados</div>
            </div>
            <div className="border border-border bg-card p-5">
              <Distribution items={session.distribution} alternatives={alternatives} revealed={session.answer_revealed} />
            </div>
            {session.phase !== "finished" && (
              <div className="border border-border bg-white p-5 text-center">
                <img src={session.qr_code_data_url} alt="QR da sala" className="w-44 h-44 mx-auto" />
                <div className="font-mono-data text-2xl tracking-widest mt-2">{session.join_code}</div>
              </div>
            )}
            {session.phase === "finished" && (
              <div className="border border-emerald-600 bg-emerald-50 p-5">
                <CheckCircle size={30} weight="fill" className="text-emerald-700" />
                <div className="font-bold mt-3">Resultados registrados</div>
                <div className="text-sm mt-1">{session.materialized_corrections || session.participant_count} correções foram integradas à prova normal.</div>
              </div>
            )}
          </aside>
        </section>
      )}
    </main>
  );
}
